/**
 * The schema-conformance checks (`27_analytics_experiments.md` ANA-010, ANA-020, ANA-311).
 *
 * `checkEvent` returns every problem with an event: an unregistered, withdrawn or
 * not-mirrored name; a missing or bad envelope field; a player field that is not null on an
 * admin or system event; a property that is not registered for the event or not
 * snake_case; a value outside the values `27` lists. CI fails any event fired in a test
 * that has a problem (ANA-010 Accept; ANA-020 Accept).
 */

import {
  AGE_BANDS,
  ENVELOPE_FIELDS,
  GOLFER_ROLES,
  HUBS,
  isUtcTimestamp,
  PLATFORMS,
  PLAYER_FIELDS,
  WORLD_KINDS,
  type Envelope,
} from './envelope';
import {
  CLIENT_SUPER_PROPERTIES,
  EVENTS,
  NOT_ANALYTICS_EVENT_NAMES,
  WITHDRAWN_EVENT_NAMES,
} from './schema';
import type { EventSpec, PropertySpec, PropertyValue } from './types';

/** Every registered event name. */
export type EventName = keyof typeof EVENTS;

/** Every registered event name, in the order of `27`'s tables. */
export const EVENT_NAMES = Object.keys(EVENTS) as readonly EventName[];

type Spec<N extends EventName> = (typeof EVENTS)[N];
type ValueOf<P> = P extends { readonly values: readonly (infer V)[] }
  ? V | null
  : P extends { readonly boolean: true }
    ? boolean | null
    : PropertyValue;

/** The extra properties an event may carry, typed from the schema. */
export type PropertiesOf<N extends EventName> = {
  readonly [K in keyof Spec<N>['properties']]?: ValueOf<Spec<N>['properties'][K]>;
};

/** An analytics event as the forwarder and the client SDK send it. */
export interface AnalyticsEvent {
  readonly event: string;
  readonly envelope: Envelope;
  readonly properties: Readonly<Record<string, PropertyValue>>;
}

export type ProblemCode =
  | 'unknown_event'
  | 'withdrawn_event'
  | 'not_analytics_event'
  | 'bad_event_name'
  | 'missing_envelope_field'
  | 'bad_envelope_value'
  | 'player_field_not_null'
  | 'unknown_property'
  | 'bad_property_name'
  | 'bad_property_value';

export interface Problem {
  readonly code: ProblemCode;
  readonly detail: string;
}

const SNAKE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
const withdrawn: ReadonlySet<string> = new Set(WITHDRAWN_EVENT_NAMES);
const notAnalytics: ReadonlySet<string> = new Set(NOT_ANALYTICS_EVENT_NAMES);
const superProps: ReadonlySet<string> = new Set(CLIENT_SUPER_PROPERTIES);
const registry: Readonly<Record<string, EventSpec>> = EVENTS;

/** True when `name` is a registered event. */
export function isRegisteredEvent(name: string): name is EventName {
  return Object.prototype.hasOwnProperty.call(registry, name);
}

/** True when `name` is withdrawn: the schema refuses it (ANA-311 a; ANA-T21). */
export function isWithdrawnEvent(name: string): boolean {
  return withdrawn.has(name);
}

/** The registered spec of an event, or undefined. */
export function eventSpec(name: string): EventSpec | undefined {
  return isRegisteredEvent(name) ? registry[name] : undefined;
}

/** True for an event that may carry the client super-properties of `24` §9. */
function mayCarrySuperProperties(spec: EventSpec): boolean {
  return spec.src === '' || /client/.test(spec.src);
}

function checkEnvelope(
  env: Readonly<Record<string, unknown>>,
  spec: EventSpec | undefined,
  out: Problem[],
): void {
  for (const f of ENVELOPE_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(env, f))
      out.push({ code: 'missing_envelope_field', detail: f });
  }
  const oneOf = (field: string, values: readonly string[], nullable: boolean): void => {
    const v = env[field];
    if (v === undefined) return;
    if (v === null ? !nullable : typeof v !== 'string' || !values.includes(v)) {
      out.push({ code: 'bad_envelope_value', detail: field + ' = ' + String(v) });
    }
  };
  oneOf('platform', PLATFORMS, false);
  oneOf('hub', HUBS, false);
  oneOf('world_kind', WORLD_KINDS, false);
  oneOf('golfer_role', GOLFER_ROLES, true);
  oneOf('age_band', AGE_BANDS, true);
  const text = (field: string, nullable: boolean): void => {
    const v = env[field];
    if (v === undefined) return;
    if (v === null ? !nullable : typeof v !== 'string' || v.length === 0) {
      out.push({ code: 'bad_envelope_value', detail: field + ' = ' + String(v) });
    }
  };
  text('account_id', true);
  text('golfer_id', true);
  text('session_id', true);
  text('app_version', false);
  const ts = env['ts'];
  if (ts !== undefined && (typeof ts !== 'string' || !isUtcTimestamp(ts))) {
    out.push({ code: 'bad_envelope_value', detail: 'ts = ' + String(ts) });
  }
  if (spec?.system === true) {
    for (const f of PLAYER_FIELDS) {
      if (env[f] !== undefined && env[f] !== null)
        out.push({ code: 'player_field_not_null', detail: f });
    }
  }
}

function checkValue(name: string, p: PropertySpec, v: PropertyValue, out: Problem[]): void {
  if (v === null) return;
  if (p.values !== undefined) {
    const values = p.values;
    // a list property ("a list of `a`\|`b`") holds values of the list
    const ok = Array.isArray(v)
      ? v.every((x) => typeof x === 'string' && values.includes(x))
      : typeof v === 'string' && values.includes(v);
    if (!ok) out.push({ code: 'bad_property_value', detail: name + ' = ' + JSON.stringify(v) });
  }
  if (p.boolean === true && typeof v !== 'boolean') {
    out.push({ code: 'bad_property_value', detail: name + ' = ' + JSON.stringify(v) });
  }
}

/** Every problem with one event (ANA-010, ANA-020, ANA-311). An empty list means it conforms. */
export function checkEvent(e: AnalyticsEvent): Problem[] {
  const out: Problem[] = [];
  if (!SNAKE.test(e.event)) out.push({ code: 'bad_event_name', detail: e.event });
  const spec = eventSpec(e.event);
  if (spec === undefined) {
    if (withdrawn.has(e.event)) out.push({ code: 'withdrawn_event', detail: e.event });
    else if (notAnalytics.has(e.event)) out.push({ code: 'not_analytics_event', detail: e.event });
    else out.push({ code: 'unknown_event', detail: e.event });
  }
  checkEnvelope(e.envelope as unknown as Readonly<Record<string, unknown>>, spec, out);
  for (const [name, value] of Object.entries(e.properties)) {
    if (!SNAKE.test(name)) {
      out.push({ code: 'bad_property_name', detail: name });
      continue;
    }
    if (spec === undefined) continue;
    const p = spec.properties[name];
    if (p !== undefined) {
      checkValue(name, p, value, out);
    } else if (!(superProps.has(name) && mayCarrySuperProperties(spec))) {
      out.push({ code: 'unknown_property', detail: e.event + '.' + name });
    }
  }
  return out;
}

/**
 * Builds an event with the envelope and checks it. Throws on any problem, so that code
 * cannot send an event the schema does not hold (ANA-020).
 */
export function makeEvent<N extends EventName>(
  event: N,
  envelope: Envelope,
  properties: PropertiesOf<N>,
): AnalyticsEvent {
  const e: AnalyticsEvent = {
    event,
    envelope,
    properties: properties as Readonly<Record<string, PropertyValue>>,
  };
  const problems = checkEvent(e);
  if (problems.length > 0) {
    throw new Error(
      'analytics: ' + event + ': ' + problems.map((p) => p.code + ' ' + p.detail).join('; '),
    );
  }
  return e;
}
